import { resolveInvoiceLineItemsFromRecord } from "../../../../utilis/invoiceEditHelpers";

/**
 * Student discount & commission balance calculations for commission plans,
 * installments, and commission invoices.
 *
 * Base rule: all discount math starts from commissionable tuition fee
 * (installment tuition_amount or invoice line unit_price).
 *
 * Terminology (per business):
 * - student_discount  : discount payable on full commissionable fee (fee × %)
 * - discount_paid     : discount already applied on paid tuition (paid × %)
 * - discount_balance  : student_discount − discount_paid
 * - balance_fee       : commissionable_fee − paid_fee
 * - net_commission    : expected_commission − discount_paid (our commission after student discount)
 * - balance_commission: net_commission − received_commission (capped at 0)
 */

function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export function resolveDiscountPercent(...sources) {
  for (const source of sources) {
    if (source === "" || source == null || source === undefined) continue;
    const num = Number(source);
    if (Number.isFinite(num) && num >= 0) return Math.min(num, 100);
  }
  return 0;
}

/** Discount amount from commissionable fee × percent. */
export function calculateDiscountFromFee(fee, discountPercent) {
  const base = Math.max(toNumber(fee), 0);
  const pct = resolveDiscountPercent(discountPercent);
  if (base <= 0 || pct <= 0) return 0;
  return roundMoney((base * pct) / 100);
}

/** Fixed plan discount → line price ratio (6 decimal places). */
export function resolvePlanFixedDiscountRatio(plan = {}) {
  const fixedAmount = toNumber(
    plan.student_discount_amount ?? plan.studentDiscountAmount,
  );
  const commissionable = Math.max(
    toNumber(
      plan.commissionable_tuition_fee ?? plan.commissionableTuitionFee,
    ),
    0,
  );
  if (fixedAmount <= 0 || commissionable <= 0) return 0;
  return Math.round((fixedAmount / commissionable) * 1_000_000) / 1_000_000;
}

function resolvePlanStudentDiscountType(plan = {}) {
  return String(
    plan.student_discount_type ?? plan.studentDiscountType ?? "",
  ).toLowerCase();
}

/**
 * Student discount for one commission installment invoice line.
 * Percentage: line tuition price × plan %.
 * Fixed: line tuition price × (plan fixed amount / plan commissionable tuition).
 */
export function calculateCommissionInstallmentLineDiscount(
  linePrice,
  plan = {},
) {
  const price = Math.max(toNumber(linePrice), 0);
  if (price <= 0) return 0;

  const discountType = resolvePlanStudentDiscountType(plan);

  if (discountType === "fixed") {
    const ratio = resolvePlanFixedDiscountRatio(plan);
    if (ratio <= 0) return 0;
    return roundMoney(price * ratio);
  }

  const pct = resolveDiscountPercent(
    plan.student_discount_percent,
    plan.studentDiscountPercent,
  );
  if (pct <= 0) return 0;
  return calculateDiscountFromFee(price, pct);
}

/** Apply plan-based student discount fields on a commission installment line. */
export function applyCommissionInstallmentLineDiscount(row = {}, plan = {}) {
  const isCommissionLine =
    Number(row.commission_installment_id ?? row.commissionInstallmentId) > 0 ||
    String(row.fee_type ?? row.feeType ?? "").toLowerCase() === "tuition";

  if (!isCommissionLine) {
    return {
      ...row,
      discount: "",
      discount_percent: "",
      discount_custom_amount: "",
      student_discount: "",
    };
  }

  const linePrice = Math.max(toNumber(row.unit_price), 0);
  const amount = calculateCommissionInstallmentLineDiscount(linePrice, plan);
  const discountType = resolvePlanStudentDiscountType(plan);
  const storedPercent =
    discountType === "percentage"
      ? resolveDiscountPercent(
          plan.student_discount_percent,
          plan.studentDiscountPercent,
        )
      : linePrice > 0 && amount > 0
        ? roundMoney((amount / linePrice) * 100)
        : 0;

  return {
    ...row,
    discount_tab: "percentage",
    discount_rule_id: "",
    discount_custom_amount: "",
    discount_percent: storedPercent > 0 ? String(storedPercent) : "",
    discount: amount > 0 ? String(amount) : "",
    student_discount: amount > 0 ? amount : 0,
  };
}

/**
 * Full discount metrics for one installment or invoice commission line.
 * Pass stored API values when present; missing values are derived.
 */
export function calculateCommissionDiscountMetrics({
  commissionableFee = 0,
  paidFee = 0,
  discountPercent = 0,
  expectedCommission = 0,
  receivedCommission = 0,
  studentDiscount = null,
  discountPaid = null,
  discountBalance = null,
  balanceFee = null,
  netCommission = null,
  balanceCommission = null,
} = {}) {
  const fee = Math.max(toNumber(commissionableFee), 0);
  const paid = Math.max(toNumber(paidFee), 0);
  const pct = resolveDiscountPercent(discountPercent);
  const expected = Math.max(toNumber(expectedCommission), 0);
  const received = Math.max(toNumber(receivedCommission), 0);

  const computedStudentDiscount = calculateDiscountFromFee(fee, pct);
  const computedDiscountPaid = calculateDiscountFromFee(
    Math.min(paid, fee),
    pct,
  );
  const computedBalanceFee = roundMoney(Math.max(fee - paid, 0));
  const computedDiscountBalance = roundMoney(
    Math.max(computedStudentDiscount - computedDiscountPaid, 0),
  );
  const computedNetCommission = roundMoney(
    Math.max(expected - computedDiscountPaid, 0),
  );
  const computedBalanceCommission = roundMoney(
    Math.max(computedNetCommission - received, 0),
  );

  return {
    discountPercent: pct,
    commissionableFee: fee,
    paidFee: paid,
    studentDiscount:
      studentDiscount != null && studentDiscount !== ""
        ? roundMoney(toNumber(studentDiscount))
        : computedStudentDiscount,
    discountPaid:
      discountPaid != null && discountPaid !== ""
        ? roundMoney(toNumber(discountPaid))
        : computedDiscountPaid,
    discountBalance:
      discountBalance != null && discountBalance !== ""
        ? roundMoney(toNumber(discountBalance))
        : computedDiscountBalance,
    balanceFee:
      balanceFee != null && balanceFee !== ""
        ? roundMoney(toNumber(balanceFee))
        : computedBalanceFee,
    netCommission:
      netCommission != null && netCommission !== ""
        ? roundMoney(toNumber(netCommission))
        : computedNetCommission,
    balanceCommission:
      balanceCommission != null && balanceCommission !== ""
        ? roundMoney(toNumber(balanceCommission))
        : computedBalanceCommission,
  };
}

/** Map saved installment row + plan default % → metrics. */
export function calculateInstallmentDiscountMetrics(row = {}, plan = {}) {
  const fee = Math.max(
    toNumber(row.tuitionAmount ?? row.tuition_amount),
    0,
  );
  let paidFee = Math.max(
    toNumber(row.studentPaidTuition ?? row.student_paid_tuition),
    0,
  );
  const storedStudentDiscount = toNumber(
    row.studentDiscount ?? row.student_discount,
  );

  let discountPercent = resolveDiscountPercent(
    row.studentDiscountPercent,
    row.student_discount_percent,
    row.discount_percent,
    row.discountPercent,
    plan.studentDiscountPercent,
    plan.student_discount_percent,
  );

  // Backfill % from stored student_discount when migrating old data.
  if (discountPercent <= 0 && fee > 0 && storedStudentDiscount > 0) {
    discountPercent = roundMoney((storedStudentDiscount / fee) * 100);
  }

  return calculateCommissionDiscountMetrics({
    commissionableFee: fee,
    paidFee,
    discountPercent,
    expectedCommission: row.commissionAmount ?? row.commission_amount,
    receivedCommission: row.commissionReceived ?? row.commission_received,
    studentDiscount: row.studentDiscount ?? row.student_discount,
    discountPaid: row.discountPaid ?? row.discount_paid,
    discountBalance: row.discountBalance ?? row.discount_balance,
    balanceFee: row.balanceFee ?? row.balance_fee,
    netCommission: row.netCommission ?? row.net_commission,
    balanceCommission: row.balanceCommission ?? row.balance_commission,
  });
}

/** Invoice commission line — paid metrics stay 0 until invoice payment is recorded. */
export function calculateInvoiceCommissionLineMetrics(row = {}, plan = {}) {
  const qty = Math.max(toNumber(row.quantity) || 0, 0);
  const unitPrice = toNumber(row.unit_price);
  const commissionableFee = unitPrice;
  const paidFee = Math.max(toNumber(row.paid_fee ?? row.paidFee), 0);
  const expectedCommission = roundMoney(unitPrice * qty);

  const storedStudentDiscount = row.student_discount ?? row.studentDiscount;
  const studentDiscount =
    storedStudentDiscount != null && storedStudentDiscount !== ""
      ? roundMoney(toNumber(storedStudentDiscount))
      : calculateCommissionInstallmentLineDiscount(unitPrice, plan);

  const discountPercent = resolveDiscountPercent(
    row.discount_percent,
    row.discountPercent,
    plan.student_discount_percent,
    plan.studentDiscountPercent,
  );

  return calculateCommissionDiscountMetrics({
    commissionableFee,
    paidFee,
    discountPercent,
    expectedCommission,
    receivedCommission: 0,
    studentDiscount,
    discountPaid: row.discount_paid ?? row.discountPaid,
    discountBalance: row.discount_balance ?? row.discountBalance,
    balanceFee: row.balance_fee ?? row.balanceFee,
    netCommission: row.net_commission ?? row.netCommission,
  });
}

/** Sync derived discount fields onto a commission invoice line before save. */
export function syncCommissionInvoiceLineMetrics(
  row = {},
  plan = {},
  { forceRecalculate = false } = {},
) {
  const savedDiscount = toNumber(
    row.student_discount ?? row.studentDiscount ?? row.discount,
  );
  const withDiscount =
    !forceRecalculate && savedDiscount > 0
      ? row
      : applyCommissionInstallmentLineDiscount(row, plan);
  const feeType = String(withDiscount.fee_type ?? withDiscount.feeType ?? "").toLowerCase();
  const isCommissionLine =
    Number(withDiscount.commission_installment_id ?? withDiscount.commissionInstallmentId) > 0 ||
    feeType === "tuition";

  if (!isCommissionLine) return withDiscount;

  const metrics = calculateInvoiceCommissionLineMetrics(withDiscount, plan);
  return {
    ...withDiscount,
    student_discount: metrics.studentDiscount,
    discount_paid: metrics.discountPaid,
    discount_balance: metrics.discountBalance,
    balance_fee: metrics.balanceFee,
    net_commission: metrics.netCommission,
    discount:
      metrics.studentDiscount > 0 ? String(metrics.studentDiscount) : "",
  };
}

function isActiveCommissionInvoiceStatus(status = "") {
  const normalized = String(status || "").toLowerCase();
  return !["cancelled", "void", "refunded"].includes(normalized);
}

/**
 * Student discount shown on an invoice tuition line is on the full tuition
 * amount; only the commission share (tuition × qty) is given up from commission.
 */
export function resolveCommissionDiscountImpactFromLine(line = {}) {
  const commissionId = Number(
    line.commission_installment_id ?? line.commissionInstallmentId,
  );
  const feeType = String(line.fee_type ?? line.feeType ?? "").toLowerCase();
  if (!commissionId && feeType !== "tuition") return 0;

  const qty = Math.max(toNumber(line.quantity ?? line.qty) || 0, 0);
  const unit = Math.max(toNumber(line.unit_price ?? line.unitPrice) || 0, 0);
  const tuitionDiscount = Math.max(
    toNumber(line.student_discount ?? line.studentDiscount ?? line.discount),
    0,
  );
  if (tuitionDiscount <= 0) return 0;

  const effectiveQty = qty > 0 ? qty : 1;
  const lineCommission = roundMoney(unit * effectiveQty);
  if (lineCommission <= 0) return 0;

  // Percentage plans: qty is commission rate (e.g. 0.1 for 10%).
  if (qty > 0 && qty <= 1) {
    return roundMoney(tuitionDiscount * qty);
  }

  return roundMoney(Math.min(tuitionDiscount, lineCommission));
}

function isCommissionTuitionInvoiceLineForSummary(line = {}) {
  const commissionId = Number(
    line.commission_installment_id ?? line.commissionInstallmentId,
  );
  const feeType = String(line.fee_type ?? line.feeType ?? "").toLowerCase();
  return commissionId > 0 || feeType === "tuition";
}

/** Invoice line student discount (same $ as on the invoice — display only). */
export function resolveCommissionStudentDiscountFromLine(line = {}) {
  if (!isCommissionTuitionInvoiceLineForSummary(line)) return 0;
  return Math.max(
    toNumber(line.student_discount ?? line.studentDiscount ?? line.discount),
    0,
  );
}

/** Sum student discount on commission invoice tuition lines (matches invoice UI). */
export function sumCommissionStudentDiscountFromInvoices(invoices = []) {
  let total = 0;

  (Array.isArray(invoices) ? invoices : []).forEach((invoiceRow) => {
    const invoiceRecord = invoiceRow?.raw || invoiceRow?.wrapper || invoiceRow;
    const status = String(
      invoiceRow?.status ?? invoiceRecord?.status ?? "",
    ).toLowerCase();
    if (!isActiveCommissionInvoiceStatus(status)) return;

    const recordLines = resolveInvoiceLineItemsFromRecord(invoiceRecord);
    const wrapperLines =
      invoiceRow?.wrapper && invoiceRow.wrapper !== invoiceRecord
        ? resolveInvoiceLineItemsFromRecord(invoiceRow.wrapper)
        : [];
    const lines =
      wrapperLines.length > recordLines.length ? wrapperLines : recordLines;

    lines.forEach((line) => {
      total += resolveCommissionStudentDiscountFromLine(line);
    });
  });

  return roundMoney(total);
}

/** Sum commission sacrificed on saved commission invoices (display only). */
export function sumCommissionDiscountImpactFromInvoices(invoices = []) {
  let total = 0;

  (Array.isArray(invoices) ? invoices : []).forEach((invoiceRow) => {
    const invoiceRecord = invoiceRow?.raw || invoiceRow?.wrapper || invoiceRow;
    const status = String(
      invoiceRow?.status ?? invoiceRecord?.status ?? "",
    ).toLowerCase();
    if (!isActiveCommissionInvoiceStatus(status)) return;

    const recordLines = resolveInvoiceLineItemsFromRecord(invoiceRecord);
    const wrapperLines =
      invoiceRow?.wrapper && invoiceRow.wrapper !== invoiceRecord
        ? resolveInvoiceLineItemsFromRecord(invoiceRow.wrapper)
        : [];
    const lines =
      wrapperLines.length > recordLines.length ? wrapperLines : recordLines;

    lines.forEach((line) => {
      total += resolveCommissionDiscountImpactFromLine(line);
    });
  });

  return roundMoney(total);
}

/** Aggregate plan-level totals from installment metrics. */
export function aggregateInstallmentDiscountMetrics(rows = [], plan = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const metrics = list.map((row) =>
    calculateInstallmentDiscountMetrics(row, plan),
  );

  const sum = (key) =>
    roundMoney(metrics.reduce((total, row) => total + toNumber(row[key]), 0));

  const totalExpectedCommission = roundMoney(
    list.reduce(
      (total, row) =>
        total + toNumber(row.commissionAmount ?? row.commission_amount),
      0,
    ),
  );

  return {
    installmentCount: list.length,
    totalCommissionableFee: sum("commissionableFee"),
    totalPaidFee: sum("paidFee"),
    totalStudentDiscount: sum("studentDiscount"),
    totalDiscountPaid: sum("discountPaid"),
    totalDiscountBalance: sum("discountBalance"),
    totalBalanceFee: sum("balanceFee"),
    totalExpectedCommission,
    totalNetCommission: sum("netCommission"),
    totalBalanceCommission: sum("balanceCommission"),
    rows: metrics,
  };
}
